import React, { FC } from 'react';
import styled from 'styled-components';
import { TFunction } from 'react-i18next';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface TooltipData {
  signal: number;
  base: number;
  high?: number;
  low?: number;
  unit: string;
  formatConcentration: (value: number) => string;
}

interface OvCalibrationChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: unknown;
  }>;
  unit: string;
  formatConcentration: (value: number) => string;
  t: TFunction;
  // Data extractor functions for different chart types
  extractData: (payload: unknown) => TooltipData;
}

/**
 * Shared tooltip component for calibration charts
 * Displays signal, concentration values, and uncertainty in ± format
 */
const OvCalibrationChartTooltip: FC<OvCalibrationChartTooltipProps> = ({
  active,
  payload,
  unit,
  formatConcentration,
  t,
  extractData,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = extractData(payload[0].payload);
  const { signal, base, high, low } = data;
  const displayUnit = data.unit || unit;

  // Check if error margin data is available
  const hasErrorData = high !== undefined && low !== undefined;

  // Calculate uncertainty as ± value (difference from base to high or low)
  // Typically: uncertainty = (high - base) = (base - low), displayed as ±X
  // Ensure uncertainty is always positive (use absolute values)
  const uncertainty = hasErrorData
    ? Math.max(
        Math.abs(high! - base),
        Math.abs(base - low!)
      )
    : undefined;

  const explanationText = hasErrorData
    ? t('lots.calibration.chart.uncertaintyExplanation', {
        min: formatConcentration(low!),
        max: formatConcentration(high!),
      })
    : undefined;

  return (
    <TooltipContainer>
      <TooltipTitle>
        {t('lots.calibration.chart.signal')}: {signal}
      </TooltipTitle>
      {hasErrorData && (
        <TooltipRow>
          <TooltipLabel>{t('lots.calibration.chart.high')}:</TooltipLabel>
          <TooltipValue>{formatConcentration(high!)} {displayUnit}</TooltipValue>
        </TooltipRow>
      )}
      <TooltipRow>
        <TooltipLabel bold>{t('lots.calibration.chart.base')}:</TooltipLabel>
        <TooltipValue bold>{formatConcentration(base)} {displayUnit}</TooltipValue>
      </TooltipRow>
      {hasErrorData && (
        <>
          <TooltipRow>
            <TooltipLabel>{t('lots.calibration.chart.low')}:</TooltipLabel>
            <TooltipValue>{formatConcentration(low!)} {displayUnit}</TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>{t('lots.calibration.chart.uncertainty')}:</TooltipLabel>
            <TooltipValue>±{formatConcentration(uncertainty!)} {displayUnit}</TooltipValue>
          </TooltipRow>
        </>
      )}
      {explanationText && (
        <TooltipExplanation>{explanationText}</TooltipExplanation>
      )}
    </TooltipContainer>
  );
};

export default OvCalibrationChartTooltip;

const TooltipContainer = styled.div`
  background-color: ${Colours.WHITE};
  border: 1px solid ${Colours.GRAY['300']};
  border-radius: ${Variables.borderRadius.SMALL};
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  max-width: 280px;
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-weight: bold;
  color: ${Colours.OV_BASE};
  margin-bottom: 8px;
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 12px;
`;

const TooltipLabel = styled.span<{ bold?: boolean }>`
  color: ${Colours.OV_BASE};
  font-weight: ${(props) => (props.bold ? 'bold' : 'normal')};
  margin-right: 8px;
`;

const TooltipValue = styled.span<{ bold?: boolean }>`
  color: ${Colours.OV_BASE};
  font-weight: ${(props) => (props.bold ? 'bold' : 'normal')};
`;

const TooltipExplanation = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${Colours.GRAY['200']};
  font-size: 11px;
  color: ${Colours.GRAY['600']};
  font-style: italic;
  line-height: 1.4;
`;

